import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  switchMap,
  map,
  catchError,
  filter,
  debounceTime
} from 'rxjs/operators';
import { FavoriteOccupationsService } from '@cpd-app/core-module/services';

import * as FavoriteOccupationsActions from './favorite-occupations.actions';
import * as UserProfileActions from '../user-profile/user-profile.actions';
import { ErrorReceived } from '../error/error.actions';
import { UserProfileFacade } from '../user-profile';

@Injectable()
export class FavoriteOccupationsEffects {
  public loadFavoriteOccupations$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        UserProfileActions.saveUserProfileToState,
        FavoriteOccupationsActions.favoriteOccupationAddedSuccess,
        FavoriteOccupationsActions.favoriteOccupationRemovedSuccess
      ),
      debounceTime(500),
      /*
       * CPD-654. This waited on `profileWorkTypeSet$`, and a member whose
       * work type was never set therefore never loaded their favourites --
       * on dev, an account with a finished skills survey and 3/3 goals still
       * had `profileWorkType.type` null, and `FavoriteOccupations` was never
       * put on the wire at all. The list looked empty after every refresh.
       *
       * It also broke the three-favourite cap, which is the same defect
       * wearing a different hat: `selectIsFavoriteOccupationsAtMaxCount`
       * counts the entity store this effect fills, so a store that never
       * loads reports zero and the cap never engages. There is no server-side
       * cap to catch it (see `UserProfileService.addFavoriteOccupation`).
       *
       * What this actually needs is a profile to belong to, not a work type.
       */
      switchMap(() => this.userProfileFacade.userIdSet$),
      filter((userIdSet) => userIdSet),
      switchMap(() =>
        this.favoriteOccupationsService.getFavoriteOccupations().pipe(
          map((favoriteOccupations) =>
            FavoriteOccupationsActions.favoriteOccupationsLoadedSuccess({
              favoriteOccupations
            })
          ),
          catchError((error) => of(ErrorReceived({ error })))
        )
      )
    );
  });

  public addFavoriteOccupation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoriteOccupationsActions.addFavoriteOccupation),
      switchMap(({ onetCode, jobId }) =>
        this.favoriteOccupationsService
          .addFavoriteOccupation(onetCode, jobId)
          .pipe(
            map((favoriteOccupation) =>
              FavoriteOccupationsActions.favoriteOccupationAddedSuccess({
                favoriteOccupation
              })
            ),
            catchError((error) => of(ErrorReceived({ error })))
          )
      )
    );
  });

  public removeFavoriteOccupation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(FavoriteOccupationsActions.removeFavoriteOccupation),
      switchMap(({ onetCode, jobId }) =>
        this.favoriteOccupationsService
          .removeFavoriteOccupation(onetCode, jobId)
          .pipe(
            map(() =>
              FavoriteOccupationsActions.favoriteOccupationRemovedSuccess({
                onetCode,
                jobId
              })
            ),
            catchError((error) => of(ErrorReceived({ error })))
          )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private favoriteOccupationsService: FavoriteOccupationsService,
    private userProfileFacade: UserProfileFacade
  ) {}
}
